/**
 * Enhanced Logging Utility for SSO and MongoDB Operations
 * 
 * This utility provides structured logging with emojis and colors
 * for better debugging and monitoring of the SSO authentication flow
 */

class Logger {
  constructor() {
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  // SSO Authentication Logs
  ssoStart(message, data = {}) {
    if (!this.isEnabled) return;
    console.group('🚀 SSO Authentication Started');
    console.log(message);
    if (Object.keys(data).length > 0) {
      console.table(data);
    }
  }

  ssoSuccess(message, data = {}) {
    if (!this.isEnabled) return;
    console.log('✅', message);
    if (Object.keys(data).length > 0) {
      console.table(data);
    }
  }

  ssoError(message, error) {
    if (!this.isEnabled) return;
    console.error('❌', message);
    console.error('Error Details:', error);
  }

  ssoEnd() {
    if (!this.isEnabled) return;
    console.groupEnd();
  }

  // MongoDB Query Logs
  mongoStart(instanceName, query) {
    if (!this.isEnabled) return;
    console.group(`🗄️ MongoDB Query - ${instanceName}`);
    console.log('Query:', JSON.stringify(query, null, 2));
  }

  mongoResult(result) {
    if (!this.isEnabled) return;
    if (result) {
      console.log('✅ User found:', {
        email: result.email,
        id: result._id,
        isActive: !result._isDeleted,
        roles: result.roles?.length || 0
      });
    } else {
      console.log('❌ User not found');
    }
  }

  mongoEnd() {
    if (!this.isEnabled) return;
    console.groupEnd();
  }

  // User Verification Logs
  verificationStart(email) {
    if (!this.isEnabled) return;
    console.group(`🔍 User Verification - ${email}`);
    console.log('Starting verification across all instances...');
  }

  verificationResult(instances) {
    if (!this.isEnabled) return;
    console.log(`📊 Verification Summary: ${instances.length} accessible instances`);
    if (instances.length > 0) {
      console.table(instances.map(inst => ({
        Instance: inst.name,
        Role: inst.userRole,
        URL: inst.url,
        LastAccess: inst.lastAccess
      })));
    }
  }

  verificationEnd() {
    if (!this.isEnabled) return;
    console.groupEnd();
  }

  // General logging methods
  info(message, data = {}) {
    if (!this.isEnabled) return;
    console.log('ℹ️', message);
    if (Object.keys(data).length > 0) {
      console.log(data);
    }
  }

  warn(message, data = {}) {
    if (!this.isEnabled) return;
    console.warn('⚠️', message);
    if (Object.keys(data).length > 0) {
      console.warn(data);
    }
  }

  error(message, error) {
    if (!this.isEnabled) return;
    console.error('💥', message);
    if (error) {
      console.error(error);
    }
  }

  // MongoDB Query Templates for Documentation
  logMongoQueryTemplates() {
    if (!this.isEnabled) return;
    
    console.group('📋 MongoDB Query Templates');
    console.log('These are the actual MongoDB queries that would be executed in production:');
    
    console.log('\n1. Find User by Email (Case-Insensitive):');
    console.log(JSON.stringify({
      email: { $regex: new RegExp(`^user@example.com$`, 'i') },
      _isDeleted: false
    }, null, 2));

    console.log('\n2. Aggregation with Role Population:');
    console.log(JSON.stringify([
      {
        $match: {
          email: { $regex: new RegExp(`^user@example.com$`, 'i') },
          _isDeleted: false
        }
      },
      {
        $lookup: {
          from: 'roles',
          localField: 'roles',
          foreignField: '_id',
          as: 'roleDetails'
        }
      },
      {
        $project: {
          _id: 1,
          email: 1,
          auth: 1,
          firstAccess: 1,
          lastAccess: 1,
          idNumber: 1,
          _isDeleted: 1,
          roles: '$roleDetails.name'
        }
      }
    ], null, 2));

    console.log('\n3. Update Last Access:');
    console.log(JSON.stringify({
      $set: {
        lastAccess: new Date()
      }
    }, null, 2));

    console.groupEnd();
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;
